import { useState, useEffect } from 'react';
import { CheckIcon, CloseIcon } from './Icons.jsx';
import './ExprQuestion.css';

/**
 * Fill the operator into each expression so the line reaches its target.
 * The blanks are filled from one fixed keypad at the bottom of the card
 * rather than a picker per row: on a phone that keeps the operator keys in
 * the same place under the thumb the whole time, and tapping a key fills
 * the active blank and jumps to the next empty one, so a student can answer
 * four lines with four taps. Blanks may be left empty — only that line is
 * scored wrong, not the whole question.
 */
export default function ExprQuestion({ question, answered, onSubmit }) {
  const { payload, revealed } = question;
  const isLocked = answered || Boolean(revealed);
  const items = payload.items || [];
  const keys = payload.keys || ['+', '-', '*', '/', '//', '%', '**'];

  const [choices, setChoices] = useState({});
  const [activeId, setActiveId] = useState(items[0]?.id || null);

  useEffect(() => {
    setChoices({});
    setActiveId(items[0]?.id || null);
  }, [question.id]);

  const pick = (op) => {
    if (isLocked || !activeId) return;
    const next = { ...choices, [activeId]: op };
    setChoices(next);
    const nextEmpty = items.find((item) => !next[item.id]);
    setActiveId(nextEmpty ? nextEmpty.id : activeId);
  };

  const filled = items.filter((item) => choices[item.id]).length;
  const knowsAnswers = filled > 0;

  const submit = () => {
    if (isLocked || filled === 0) return;
    onSubmit({ answers: choices });
  };

  return (
    <div className="card stack">
      <div className="row-between">
        <span className="q-kicker">เติมตัวดำเนินการ</span>
        {!isLocked && (
          <span className="expr-progress num">
            {filled}/{items.length}
          </span>
        )}
      </div>
      <h2>{payload.instruction}</h2>

      <div className="expr-list">
        {items.map((item) => {
          const picked = choices[item.id];
          // A phone that reloaded mid-question has nothing left to judge (the
          // server never sends a student's own answer back), so it shows the
          // key without claiming the student got it wrong. A blank left inside
          // an answer that WAS given still counts as wrong, as it is scored.
          const isRight = revealed && knowsAnswers && picked === item.answer;
          const isWrong = revealed && knowsAnswers && picked !== item.answer;
          return (
            <div
              key={item.id}
              className={`expr-row ${isRight ? 'is-correct' : ''} ${isWrong ? 'is-wrong' : ''}`}
            >
              <span className="expr-line num">
                <span className="expr-operand">{item.left}</span>
                <button
                  type="button"
                  className={`expr-blank ${picked ? 'is-filled' : ''} ${activeId === item.id && !isLocked ? 'is-active' : ''}`}
                  disabled={isLocked}
                  onClick={() => setActiveId(item.id)}
                >
                  {revealed && !isRight ? (
                  <>
                    {picked && <s className="expr-was">{picked}</s>}
                    {item.answer}
                  </>
                ) : (
                  picked || '?'
                )}
                </button>
                <span className="expr-operand">{item.right}</span>
                <span className="expr-eq">=</span>
                <span className="expr-target">{item.target}</span>
              </span>

              {revealed && knowsAnswers && (
                <span className="expr-mark" aria-hidden="true">
                  {isRight ? <CheckIcon size={16} /> : <CloseIcon size={16} />}
                </span>
              )}
              {revealed && item.note && <span className="expr-note">{item.note}</span>}
            </div>
          );
        })}
      </div>

      {!isLocked && (
        <>
          <div className="expr-keypad">
            {keys.map((op) => (
              <button key={op} type="button" className="expr-key num" onClick={() => pick(op)}>
                {op}
              </button>
            ))}
          </div>
          <button className="btn btn-primary btn-block" disabled={filled === 0} onClick={submit}>
            ยืนยันคำตอบ
          </button>
          {filled < items.length && filled > 0 && (
            <p className="text-dim text-center expr-hint">ยังเหลือช่องว่างอยู่ — ส่งได้ แต่ช่องที่ว่างจะไม่ได้คะแนน</p>
          )}
        </>
      )}

      {isLocked && !revealed && (
        <p className="text-dim text-center">
          {answered ? 'ส่งคำตอบแล้ว รอครูเฉลย...' : 'เฉลยคำตอบแล้ว — ปิดรับคำตอบข้อนี้'}
        </p>
      )}

      {revealed && payload.revealNote && (
        <p className="expr-success text-center row" style={{ justifyContent: 'center', gap: '6px' }}>
          <CheckIcon size={16} />
          <span>{payload.revealNote}</span>
        </p>
      )}
    </div>
  );
}
